// Performance V2 — Audience tab. Sub-views: Geography (#5922 — choropleth +
// top-locations table), Device (#5922 — per-device metrics), ICP Segments
// (placeholder), Demographics (#4298 — age / gender / parental status with
// bid adjustments). Extracted from performance-v2.tsx (#4932 Phase 0.3).
// The sub-view selector renders in the shell's header toolbar, so the active
// sub-view arrives as a prop; each sub-view's query is lazy on that sub-view
// (the module mounts only when the tab is active).
import { Spinner } from '@/components/glitch-ui/spinner';
import { useState } from 'react';
import { MapPin, TabletSmartphone, Users } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import { GeographyChoropleth } from './geography-choropleth';

import type { DemographicDimension, DemographicRow, GeographyRow, DeviceRow } from '@/lib/apis';
import {
  useAccountDemographicsReportQuery,
  useAccountGeographyReportQuery,
  useAccountDeviceReportQuery,
} from '@/lib/query-options';
import { Card } from '@/components/ui/card';
import { DataTable } from '@/components/glitch-ui/data-table';
import { cn } from '@/lib/utils';
import { explainDemographics } from '../explainers';
import { fmt, KeywordStatusBadge, EmptyTabState } from '../cells';

export type AudienceSubView = 'geography' | 'device' | 'icp-segments' | 'demographics';

export function AudienceTab({
  subView,
  campaignIds,
  currencySymbol,
  dateRangeFilter,
}: {
  subView: AudienceSubView;
  campaignIds: string | undefined;
  currencySymbol: string;
  dateRangeFilter: { start_date: string; end_date: string };
}) {
  const cs = currencySymbol;

  // #4298 — Demographics dimension picker inside the Audience › Demographics
  // sub-view. The backend keys queries by dimension, so switching the picker
  // refetches (cached per dimension by React Query).
  const [demographicDimension, setDemographicDimension] = useState<DemographicDimension>('age');

  const { data: demographicsData, isLoading: isDemographicsLoading } = useAccountDemographicsReportQuery(
    demographicDimension,
    dateRangeFilter.start_date,
    dateRangeFilter.end_date,
    campaignIds || undefined,
    { enabled: subView === 'demographics' },
  );

  // #5922 — geography + device, each lazy on its own sub-view.
  // Account-level reports (#6051): campaignIds is an optional filter, not a requirement. Gating the
  // query on it left the Audience tab empty for accounts that have a connected Google Ads account with
  // data but no glitch-managed campaigns (so the derived campaignIds is blank). Run account-wide when
  // campaignIds is absent, scoped when it's present — consistent with the Campaigns/Creative tabs.
  const { data: geographyData, isLoading: isGeographyLoading } = useAccountGeographyReportQuery(
    dateRangeFilter.start_date,
    dateRangeFilter.end_date,
    campaignIds || undefined,
    { enabled: subView === 'geography' },
  );
  const { data: deviceData, isLoading: isDeviceLoading } = useAccountDeviceReportQuery(
    dateRangeFilter.start_date,
    dateRangeFilter.end_date,
    campaignIds || undefined,
    { enabled: subView === 'device' },
  );

  const geographyColumns: ColumnDef<GeographyRow, unknown>[] = [
    { accessorKey: 'country', header: 'Country', cell: ({ row }) => (
      <span className="text-sm font-medium text-gray-900">{row.original.country}</span>
    )},
    { accessorKey: 'impressions', header: 'Imp.', cell: ({ row }) => row.original.impressions.toLocaleString() },
    { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => row.original.clicks.toLocaleString() },
    { accessorKey: 'conversions', header: 'Conv.', cell: ({ row }) => row.original.conversions.toFixed(0) },
    { accessorKey: 'ctr', header: 'CTR', cell: ({ row }) => `${(row.original.ctr * 100).toFixed(2)}%` },
    { accessorKey: 'avg_cpc', header: 'Avg. CPC', cell: ({ row }) => fmt(row.original.avg_cpc, cs) },
    { accessorKey: 'cost', header: 'Cost', cell: ({ row }) => fmt(row.original.cost, cs) },
    { accessorKey: 'conv_rate', header: 'Conv. rate', cell: ({ row }) => `${(row.original.conv_rate * 100).toFixed(1)}%` },
    { accessorKey: 'cost_per_conv', header: 'Cost/conv', cell: ({ row }) => row.original.conversions > 0 ? fmt(row.original.cost_per_conv, cs) : <span className="text-xs text-gray-400">—</span> },
  ];

  return (
    <>
      {subView === 'geography' && (() => {
        const rows = geographyData ?? [];
        const hasRows = rows.length > 0;
        if (isGeographyLoading && !hasRows) {
          return (
            <div className="flex items-center justify-center py-16">
              <Spinner size="lg" className="text-gray-400" />
              <span className="ml-3 text-sm text-gray-500">Loading geographic performance…</span>
            </div>
          );
        }
        if (!hasRows) {
          return (
            <EmptyTabState
              icon={MapPin}
              title="No Geographic Data"
              description="Location performance will appear once campaigns have impressions in the selected period."
            />
          );
        }
        const maxConversions = rows.reduce((m, r) => Math.max(m, r.conversions), 0);
        const topLocations = rows.slice(0, 8);
        return (
          <div className="grid grid-cols-12 gap-4">
            <Card className="col-span-7 p-4 border border-gray-200 shadow-sm">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Geographic Performance</h4>
              <GeographyChoropleth rows={rows} cs={cs} />
              <p className="text-[11px] text-gray-400 mt-2">Countries shaded by the selected metric. Hover for details; exact figures in the table.</p>
            </Card>

            <Card className="col-span-5 p-4 border border-gray-200 shadow-sm">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Top Locations</h4>
              <div className="space-y-2.5">
                {topLocations.map((r) => (
                  <div key={r.criterion_id}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-900 truncate max-w-[160px]">{r.country}</span>
                      <span className="text-gray-500 text-xs">
                        {r.conversions.toFixed(0)} conv · {fmt(r.cost, cs)}
                      </span>
                    </div>
                    <div className="h-1 rounded bg-gray-100 overflow-hidden mt-1">
                      <div
                        className="h-full bg-[#290BD9]"
                        style={{ width: `${maxConversions > 0 ? Math.max((r.conversions / maxConversions) * 100, 2) : 0}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <div className="col-span-12">
              <DataTable<GeographyRow>
                data={rows}
                columns={geographyColumns}
                pageSize={15}
                variant="bordered"
                emptyState="No geographic performance data available"
              />
            </div>
          </div>
        );
      })()}

      {subView === 'device' && (() => {
        const rows = deviceData ?? [];
        const hasRows = rows.length > 0;
        if (isDeviceLoading && !hasRows) {
          return (
            <div className="flex items-center justify-center py-16">
              <Spinner size="lg" className="text-gray-400" />
              <span className="ml-3 text-sm text-gray-500">Loading device performance…</span>
            </div>
          );
        }
        if (!hasRows) {
          return (
            <EmptyTabState
              icon={TabletSmartphone}
              title="No Device Data"
              description="Device performance will appear once campaigns have impressions in the selected period."
            />
          );
        }
        const totalConversions = rows.reduce((s, r) => s + r.conversions, 0);
        const deviceColumns: ColumnDef<DeviceRow, unknown>[] = [
          { accessorKey: 'device', header: 'Device', cell: ({ row }) => (
            <div className="w-40">
              <div className="text-sm font-medium text-gray-900">{row.original.device}</div>
              {/* Share of total conversions across devices. */}
              <div className="h-1 rounded bg-gray-100 overflow-hidden mt-1">
                <div
                  className="h-full bg-[#290BD9]"
                  style={{ width: `${totalConversions > 0 ? Math.max((row.original.conversions / totalConversions) * 100, 2) : 0}%` }}
                />
              </div>
            </div>
          )},
          { accessorKey: 'impressions', header: 'Imp.', cell: ({ row }) => row.original.impressions.toLocaleString() },
          { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => row.original.clicks.toLocaleString() },
          { accessorKey: 'conversions', header: 'Conv.', cell: ({ row }) => row.original.conversions.toFixed(0) },
          { accessorKey: 'ctr', header: 'CTR', cell: ({ row }) => `${(row.original.ctr * 100).toFixed(2)}%` },
          { accessorKey: 'avg_cpc', header: 'Avg. CPC', cell: ({ row }) => fmt(row.original.avg_cpc, cs) },
          { accessorKey: 'cost', header: 'Cost', cell: ({ row }) => fmt(row.original.cost, cs) },
          { accessorKey: 'conv_rate', header: 'Conv. rate', cell: ({ row }) => `${(row.original.conv_rate * 100).toFixed(1)}%` },
          { accessorKey: 'cost_per_conv', header: 'Cost/conv', cell: ({ row }) => row.original.conversions > 0 ? fmt(row.original.cost_per_conv, cs) : <span className="text-xs text-gray-400">—</span> },
        ];
        return (
          <DataTable<DeviceRow>
            data={rows}
            columns={deviceColumns}
            pageSize={10}
            variant="bordered"
            emptyState="No device performance data available"
          />
        );
      })()}

      {subView === 'icp-segments' && (
        <EmptyTabState
          icon={Users}
          title="ICP Segment Performance"
          description="Performance breakdown by Ideal Customer Profile segments will be available here. ICP campaigns are filtered from existing performance data."
        />
      )}

      {/* #4298 — Demographics sub-view. */}
      {subView === 'demographics' && (() => {
        const hasRows = Array.isArray(demographicsData) && demographicsData.length > 0;
        const DIMENSION_OPTIONS: { value: DemographicDimension; label: string }[] = [
          { value: 'age', label: 'Age' },
          { value: 'gender', label: 'Gender' },
          { value: 'parental_status', label: 'Parental status' },
        ];
        // Pretty-print Google Ads enum values. "AGE_RANGE_35_44" → "35-44",
        // "PARENT" → "Parent", "MALE" → "Male", "UNDETERMINED" → "Undetermined".
        const formatSegment = (seg: string | null): string => {
          if (!seg) return '—';
          const m = seg.match(/^AGE_RANGE_(\d+)_(\d+)$/);
          if (m) return `${m[1]}-${m[2]}`;
          if (seg === 'AGE_RANGE_65_UP') return '65+';
          if (seg === 'AGE_RANGE_UNDETERMINED' || seg === 'UNDETERMINED') return 'Unknown';
          if (seg === 'NOT_A_PARENT') return 'Not a parent';
          return seg.charAt(0) + seg.slice(1).toLowerCase().replace(/_/g, ' ');
        };
        const demographicColumns: ColumnDef<DemographicRow, unknown>[] = [
          { accessorKey: 'segment', header: ({ table: _t }) => {
            if (demographicDimension === 'age') return 'Age';
            if (demographicDimension === 'gender') return 'Gender';
            return 'Parental status';
          }, cell: ({ row }) => (
            <div>
              <div className="text-sm font-medium text-gray-900">{formatSegment(row.original.segment)}</div>
              <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5">
                <KeywordStatusBadge status={row.original.status} />
                <span className="truncate max-w-[160px]">{row.original.campaign_name}</span>
                <span className="text-gray-400">·</span>
                <span className="truncate max-w-[160px]">{row.original.ad_group_name}</span>
              </div>
            </div>
          )},
          { accessorKey: 'bid_modifier', header: 'Bid adj.', cell: ({ row }) => {
            const bm = row.original.bid_modifier;
            if (bm == null) return <span className="text-xs text-gray-400">—</span>;
            const pct = (bm - 1) * 100;
            const cls = pct > 0 ? 'text-green-700' : pct < 0 ? 'text-red-700' : 'text-gray-500';
            return <span className={cn('text-sm', cls)}>{pct === 0 ? '0%' : `${pct > 0 ? '+' : ''}${pct.toFixed(0)}%`}</span>;
          }},
          { accessorKey: 'impressions', header: 'Imp.', cell: ({ row }) => row.original.impressions.toLocaleString() },
          { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => row.original.clicks.toLocaleString() },
          { accessorKey: 'conversions', header: 'Conv.', cell: ({ row }) => row.original.conversions.toFixed(0) },
          { accessorKey: 'ctr', header: 'CTR', cell: ({ row }) => `${(row.original.ctr * 100).toFixed(2)}%` },
          { accessorKey: 'avg_cpc', header: 'Avg. CPC', cell: ({ row }) => fmt(row.original.avg_cpc, cs) },
          { accessorKey: 'cost', header: 'Cost', cell: ({ row }) => fmt(row.original.cost, cs) },
          { accessorKey: 'conv_rate', header: 'Conv. rate', cell: ({ row }) => `${(row.original.conv_rate * 100).toFixed(1)}%` },
          { accessorKey: 'cost_per_conv', header: 'Cost/conv', cell: ({ row }) => row.original.conversions > 0 ? fmt(row.original.cost_per_conv, cs) : <span className="text-xs text-gray-400">—</span> },
        ];
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-1.5 text-xs">
              <span className="text-gray-500">Group by:</span>
              {DIMENSION_OPTIONS.map(opt => {
                const active = demographicDimension === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setDemographicDimension(opt.value)}
                    className={cn(
                      'px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors',
                      active
                        ? 'bg-[#290BD9] text-white border-[#290BD9]'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300',
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {isDemographicsLoading && !hasRows ? (
              <div className="flex items-center justify-center py-16">
                <Spinner size="lg" className="text-gray-400" />
                <span className="ml-3 text-sm text-gray-500">Loading demographics…</span>
              </div>
            ) : !hasRows ? (
              <EmptyTabState
                icon={Users}
                title="No Demographic Data"
                description="Demographic performance will appear once campaigns have impressions split by the selected dimension."
              />
            ) : (
              <>
                <DataTable<DemographicRow>
                  data={demographicsData as DemographicRow[]}
                  columns={demographicColumns}
                  pageSize={20}
                  variant="bordered"
                  emptyState="No demographic performance data available"
                />
                {/* #4306 — auto-explainer footnote. */}
                {(() => {
                  const narrative = explainDemographics(demographicsData as DemographicRow[], demographicDimension);
                  return narrative ? <p className="text-xs text-gray-400 mt-2 px-1">{narrative}</p> : null;
                })()}
              </>
            )}
          </div>
        );
      })()}
    </>
  );
}
